import { Component, ChangeDetectionStrategy, input, computed, effect, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SfxService } from '../../../../service/sfx.service';

@Component({
  selector: 'lib-exam-circular-timer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  templateUrl: './exam-circular-timer.component.html',
  styleUrls: ['./exam-circular-timer.component.scss'],
})
export class ExamCircularTimerComponent {
  private sfxService = inject(SfxService);

  // --- Inputs ---
  remainingSeconds = input.required<number>();
  totalSeconds = input.required<number>();
  /** Suspend l'alarme sonore (ex. enregistrement audio en cours) */
  muteAlarm = input<boolean>(false);
  /** Affiche le libellé au-dessus de l'anneau (désactivé quand le titre est dans le header du panneau) */
  showLabel = input<boolean>(true);

  constructor() {
    this.sfxService.preload('timer_warning');

    // Effect : joue le son en boucle pendant toute la phase warning (≤ 30s),
    // sauf si l'alarme est explicitement coupée (enregistrement audio en cours).
    effect(() => {
      const state = this.timerState();
      if (state === 'warning' && !this.muteAlarm()) {
        this.sfxService.loop('timer_warning');
      } else {
        this.sfxService.stopLoop('timer_warning');
      }
    });
  }

  // --- Computed ---

  /** Texte affiché au centre : "MM:SS" */
  timeDisplay = computed(() => {
    const s = Math.max(0, this.remainingSeconds());
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  });

  /** Progression 0→1 (1 = plein, 0 = vide) */
  progress = computed(() => {
    const total = this.totalSeconds();
    if (total <= 0) return 0;
    return Math.max(0, Math.min(1, this.remainingSeconds() / total));
  });

  /** stroke-dashoffset SVG (cercle rayon 50, circumference ≈ 314.16) */
  strokeDashoffset = computed(() => {
    const circumference = 2 * Math.PI * 50;
    return circumference * (1 - this.progress());
  });

  /** Classe d'état : 'normal' | 'warning' | 'expired' */
  timerState = computed<'normal' | 'warning' | 'expired'>(() => {
    const remaining = this.remainingSeconds();
    if (remaining <= 0) return 'expired';
    if (remaining <= 30) return 'warning';
    return 'normal';
  });

  readonly circumference = 2 * Math.PI * 50;
}
